import type { BranchKey } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Popover, PopoverButton } from '@festina-life/ui/structure'
import { type MouseEvent, useRef, useState } from 'react'

import { useRebaseBranchConflictsWithBaseBranch } from '../../../../uiModels/branches/useRebaseBranchConflictsWithBaseBranch'
import { ActionsMenuPanel } from '../../../actionsPanel/ActionsMenuPanel'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { RebaseBranchForm } from './RebaseBranchForm'
import { useManageRebaseBranch } from './useManageRebaseBranch'

type Props = {
  branchKey: BranchKey
  baseBranchKey: BranchKey
}

export const RebaseBranchPopover = ({ branchKey, baseBranchKey }: Props) => {
  const translate = useTranslate()
  const popoverButtonRef = useRef<HTMLButtonElement>(null)
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false)
  const { conflicts, checkConflicts } = useRebaseBranchConflictsWithBaseBranch({
    branchKey,
    baseBranchKey,
  })
  const { rebase, isRebasing } = useManageRebaseBranch(branchKey)
  const hasConflictsWithBaseBranch = !!conflicts.length

  const handleClick = async (event: MouseEvent) => {
    // No known conflicts: check again before rebasing, as they may have appeared since the last fetch.
    event.stopPropagation()
    setIsCheckingConflicts(true)

    try {
      const latestConflicts = await checkConflicts()

      if (latestConflicts.length) {
        popoverButtonRef.current?.click()
      } else {
        await rebase(false)
        toast({
          variant: 'success',
          title: translate('branchRebaseForm.rebase.noConflicts.success', {
            branchName: branchKey.name,
            baseBranchName: baseBranchKey.name,
          }),
        })
      }
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    } finally {
      setIsCheckingConflicts(false)
    }
  }

  return (
    <Popover>
      <PopoverButton as="div" ref={popoverButtonRef}>
        <OperationsLoadedButton
          startIcon={
            hasConflictsWithBaseBranch ? 'status.warning' : 'ArrowPath'
          }
          onClick={handleClick}
          isLoading={isCheckingConflicts || isRebasing}
        >
          {translate('branchDetailsPanel.tabs.manage.rebase')}
        </OperationsLoadedButton>
      </PopoverButton>
      <ActionsMenuPanel
        anchor={{ to: 'right' }}
        className="max-w-lg! flex flex-col gap-y-4 items-end"
      >
        <RebaseBranchForm
          baseBranchKey={baseBranchKey}
          branchKey={branchKey}
          conflicts={conflicts}
        />
      </ActionsMenuPanel>
    </Popover>
  )
}
